import React, { useEffect, useState } from 'react';
import { api } from '../../App';
import toast from 'react-hot-toast';

const CHANNELS = [
  { id: 'email', label: 'Email homeowner', icon: 'fa-envelope', hint: 'Branded email + 13-page PDF attached + interactive link' },
  { id: 'sms', label: 'Text homeowner', icon: 'fa-comment-sms', hint: 'Short text with the interactive proposal link' },
  { id: 'team', label: 'Send to team', icon: 'fa-people-group', hint: 'Share with installers, closers or managers for review' },
];

export default function SendProposalModal({ proposal, onClose, onSent, delivery }) {
  const [channel, setChannel] = useState('email');
  const [to, setTo] = useState(proposal.lead_email || '');
  const [phone, setPhone] = useState(proposal.lead_phone || '');
  const [message, setMessage] = useState('');
  const [team, setTeam] = useState([]);
  const [picked, setPicked] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/api/team/members').then(r => setTeam(r.data.members || [])).catch(() => setTeam([]));
  }, []);

  const send = async () => {
    setSending(true);
    try {
      const body = { channel, message, include_pdf: true };
      if (channel === 'email') body.to = to;
      if (channel === 'sms') body.to = phone;
      if (channel === 'team') body.team_user_ids = picked;
      const r = await api.post(`/api/proposals/${proposal.id}/send`, body);
      setResult(r.data);
      if (r.data.simulated) toast(`Queued in SIMULATION mode — ${r.data.results[0]?.message}`, { icon: '⚠️', duration: 7000 });
      else toast.success(`Proposal ${channel === 'team' ? 'shared with team' : 'sent'}!`);
      onSent && onSent(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const live = channel === 'email' ? delivery?.email : channel === 'sms' ? delivery?.sms : delivery?.email;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="send-proposal-modal">
      <div className="bg-surface border border-border rounded-2xl shadow-card-hover w-full max-w-xl animate-slide-up overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-6 text-white" style={{ background: '#0C2417' }}>
          <div className="h-1 -mx-6 -mt-6 mb-5" style={{ background: 'linear-gradient(90deg,#FAAB1C 62%,#21A84A 62%)' }} />
          <p className="text-[10px] font-bold tracking-[0.2em]" style={{ color: '#FAAB1C' }}>SEND PROPOSAL</p>
          <h2 className="font-heading text-xl font-bold mt-1">{proposal.lead_name}</h2>
          <p className="text-xs text-slate-400">{proposal.lead_address} · {proposal.system_size_kw} kW · ${(proposal.net_cost || 0).toLocaleString()} net</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {CHANNELS.map(c => (
              <button key={c.id} type="button" onClick={() => setChannel(c.id)} data-testid={`send-channel-${c.id}`}
                className={`p-3 rounded-xl border text-left transition-all ${channel === c.id ? 'border-[#21A84A] bg-[#21A84A]/5' : 'border-border hover:border-[#21A84A]/40'}`}>
                <i className={`fa-solid ${c.icon} text-sm ${channel === c.id ? 'text-[#21A84A]' : 'text-text-muted'}`} />
                <p className="text-xs font-bold text-text-primary mt-1.5">{c.label}</p>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-text-muted -mt-1">{CHANNELS.find(c => c.id === channel).hint}</p>

          {channel === 'email' && (
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Recipient email</label>
              <input value={to} onChange={e => setTo(e.target.value)} className="input-field h-10 text-sm" placeholder="homeowner@email.com" data-testid="send-email-input" /></div>
          )}
          {channel === 'sms' && (
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Mobile number</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} className="input-field h-10 text-sm" placeholder="+1 (201) 555-0100" data-testid="send-phone-input" /></div>
          )}
          {channel === 'team' && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Team members</label>
              <div className="max-h-40 overflow-y-auto rounded-xl border border-border divide-y divide-border/60" data-testid="team-member-list">
                {team.length === 0 && <p className="p-3 text-xs text-text-muted">No team members found</p>}
                {team.map(u => (
                  <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-surface-alt cursor-pointer">
                    <input type="checkbox" checked={picked.includes(u.id)} onChange={e => setPicked(e.target.checked ? [...picked, u.id] : picked.filter(x => x !== u.id))} data-testid={`team-pick-${u.id}`} />
                    <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-text-primary truncate">{u.name}</p><p className="text-[10px] text-text-muted truncate">{u.email}</p></div>
                    <span className="badge text-[9px] capitalize">{u.role}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Personal note (optional)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} className="input-field h-auto py-2 text-sm resize-none" placeholder={channel === 'team' ? 'Please review roof scope before Friday…' : 'It was great meeting you today — here is everything we discussed…'} data-testid="send-message-input" /></div>

          {!live && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="send-simulation-banner">
              <i className="fa-solid fa-triangle-exclamation mt-0.5" />
              <span>{channel === 'sms' ? 'Twilio' : 'SendGrid'} isn&apos;t configured yet — this send will be logged in <b>simulation mode</b>. Add the key in Settings ▸ Key Manager to deliver for real. The share link still works immediately.</span>
            </div>
          )}
          {result && (
            <div className="p-3 rounded-xl bg-surface-alt border border-border text-xs" data-testid="send-result">
              <p className="font-bold text-text-primary capitalize"><i className={`fa-solid ${result.simulated ? 'fa-clock' : 'fa-check-circle text-green-500'} mr-1`} />{result.status} via {result.channel}</p>
              <p className="text-text-muted mt-1 break-all">Link: <a href={result.link} target="_blank" rel="noreferrer" className="text-[#21A84A] underline">{result.link}</a></p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm" data-testid="send-cancel-btn">Close</button>
            <button onClick={send} disabled={sending} className="text-sm font-bold px-5 py-2.5 rounded-xl text-[#0C2417] disabled:opacity-50" style={{ background: '#FAAB1C' }} data-testid="send-submit-btn">
              {sending ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-paper-plane mr-1.5" />Send now</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
